import { useEffect, useRef, useCallback } from 'react';
import { Application, Container, Sprite, Ticker, Graphics, Texture } from 'pixi.js';
import {
  WORLD_WIDTH,
  WORLD_HEIGHT,
  BASKET_WIDTH,
  BASKET_HEIGHT,
  BASKET_Y,
  BASKET_SPEED,
  STAR_SIZE,
  STAR_FALL_SPEED_MIN,
  STAR_FALL_SPEED_MAX,
  SPAWN_INTERVAL,
  STAR_SPAWN_PADDING,
  COLORS,
  TOUCH_SENSITIVITY,
  CATCH_PARTICLE_COUNT,
  PARTICLE_LIFETIME_MS,
} from '@/game/configs';
import { createBasketTexture, createStarTexture } from '@/game/assets';
interface Star {
  sprite: Sprite;
  speed: number;
}
interface Particle {
  graphics: Graphics;
  startX: number;
  startY: number;
  vx: number;
  vy: number;
  born: number;
  lifetime: number;
}
interface GameCanvasProps {
  onScoreChange: () => void;
  isPlaying: boolean;
}
// Procedural Web Audio catch sound
function playCatchSound(): void {
  try {
    const ctx = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = 'sine';
    osc.frequency.setValueAtTime(880, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(1320, ctx.currentTime + 0.1);
    gain.gain.setValueAtTime(0.3, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.2);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.2);
    ctx.close();
  } catch {
    // Swallow errors if audio context fails
  }
}
export function GameCanvas({ onScoreChange, isPlaying }: GameCanvasProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const appRef = useRef<Application | null>(null);
  const basketRef = useRef<Sprite | null>(null);
  const starsRef = useRef<Star[]>([]);
  const keysRef = useRef<Set<string>>(new Set());
  const lastSpawnRef = useRef(0);
  const basketTextureRef = useRef<Texture | null>(null);
  const starTextureRef = useRef<Texture | null>(null);
  const starContainerRef = useRef<Container | null>(null);
  const touchStartXRef = useRef<number | null>(null);
  const isInitializedRef = useRef(false);
  const particlesRef = useRef<Particle[]>([]);
  const particleContainerRef = useRef<Container | null>(null);
  const spawnParticleBurst = useCallback((x: number, y: number) => {
    if (!particleContainerRef.current) return;
    for (let i = 0; i < CATCH_PARTICLE_COUNT; i++) {
      const angle = (Math.PI * 2 * i) / CATCH_PARTICLE_COUNT + Math.random() * 0.5;
      const speed = 2 + Math.random() * 2;
      const size = 4 + Math.random() * 4;
      const g = new Graphics();
      g.circle(0, 0, size);
      g.fill({ color: COLORS.STAR_YELLOW });
      g.x = x;
      g.y = y;
      particleContainerRef.current.addChild(g);
      particlesRef.current.push({
        graphics: g,
        startX: x,
        startY: y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed - 1,
        born: Date.now(),
        lifetime: PARTICLE_LIFETIME_MS,
      });
    }
  }, []);
  const initGame = useCallback(async () => {
    if (!containerRef.current || appRef.current) return;
    const app = new Application();
    await app.init({
      width: WORLD_WIDTH,
      height: WORLD_HEIGHT,
      backgroundColor: COLORS.SKY_BLUE,
      antialias: true,
      resolution: Math.min(window.devicePixelRatio || 1, 2),
      autoDensity: true,
    });
    containerRef.current.appendChild(app.canvas);
    appRef.current = app;
    // Create textures using the app's renderer
    basketTextureRef.current = createBasketTexture(app.renderer);
    starTextureRef.current = createStarTexture(app.renderer);
    // Create basket
    const basket = new Sprite(basketTextureRef.current);
    basket.anchor.set(0.5);
    basket.x = WORLD_WIDTH / 2;
    basket.y = BASKET_Y;
    app.stage.addChild(basket);
    basketRef.current = basket;
    // Create star container
    const starContainer = new Container();
    app.stage.addChild(starContainer);
    starContainerRef.current = starContainer;
    // Create particle container (on top)
    const particleContainer = new Container();
    app.stage.addChild(particleContainer);
    particleContainerRef.current = particleContainer;
    // Spawn star function
    const spawnStar = () => {
      if (!starTextureRef.current || !starContainerRef.current) return;
      const star = new Sprite(starTextureRef.current);
      star.anchor.set(0.5);
      star.x = STAR_SPAWN_PADDING + Math.random() * (WORLD_WIDTH - STAR_SPAWN_PADDING * 2);
      star.y = -STAR_SIZE;
      star.scale.set(0.8 + Math.random() * 0.4);
      const speed = STAR_FALL_SPEED_MIN + Math.random() * (STAR_FALL_SPEED_MAX - STAR_FALL_SPEED_MIN);
      starContainerRef.current!.addChild(star);
      starsRef.current.push({ sprite: star, speed });
    };
    // Game loop
    app.ticker.add((ticker: Ticker) => {
      if (!isPlaying) return;
      const delta = ticker.deltaTime;
      // Move basket based on keyboard
      if (basketRef.current) {
        if (keysRef.current.has('ArrowLeft') || keysRef.current.has('a')) {
          basketRef.current.x -= BASKET_SPEED * delta;
        }
        if (keysRef.current.has('ArrowRight') || keysRef.current.has('d')) {
          basketRef.current.x += BASKET_SPEED * delta;
        }
        // Clamp basket position
        basketRef.current.x = Math.max(
          BASKET_WIDTH / 2,
          Math.min(WORLD_WIDTH - BASKET_WIDTH / 2, basketRef.current.x)
        );
      }
      // Spawn stars
      const now = Date.now();
      if (now - lastSpawnRef.current > SPAWN_INTERVAL) {
        spawnStar();
        lastSpawnRef.current = now;
      }
      // Update stars and check collision
      const basket = basketRef.current;
      const starsToRemove: number[] = [];
      starsRef.current.forEach((star, index) => {
        star.sprite.y += star.speed * delta;
        // Collision detection
        if (basket) {
          const starHalfHeight = (STAR_SIZE * star.sprite.scale.y) / 2;
          const basketHalfHeight = BASKET_HEIGHT / 2;
          const starBottom = star.sprite.y + starHalfHeight;
          const basketTop = basket.y - basketHalfHeight;
          const horizontalOverlap =
            Math.abs(star.sprite.x - basket.x) < (BASKET_WIDTH / 2 + STAR_SIZE * 0.3);
          const verticalOverlap = starBottom >= basketTop && star.sprite.y <= basket.y + basketHalfHeight;
          if (horizontalOverlap && verticalOverlap && star.sprite.y > basket.y - BASKET_HEIGHT) {
            starsToRemove.push(index);
            spawnParticleBurst(star.sprite.x, star.sprite.y);
            playCatchSound();
            onScoreChange();
            return;
          }
        }
        // Remove off-screen stars
        if (star.sprite.y > WORLD_HEIGHT + STAR_SIZE * 2) {
          starsToRemove.push(index);
        }
      });
      // Clean up collected or off-screen stars (deduplicate indices first)
      const uniqueIndices = [...new Set(starsToRemove)];
      uniqueIndices.reverse().forEach((index) => {
        const star = starsRef.current[index];
        if (star.sprite.parent) {
          star.sprite.parent.removeChild(star.sprite);
        }
        star.sprite.destroy({ children: true });
        starsRef.current.splice(index, 1);
      });
      // Update particles
      const nowMs = Date.now();
      const particlesToRemove: number[] = [];
      particlesRef.current.forEach((p, index) => {
        const age = nowMs - p.born;
        if (age >= p.lifetime) {
          particlesToRemove.push(index);
          return;
        }
        const progress = age / p.lifetime;
        p.graphics.x = p.startX + p.vx * age * 0.1;
        p.graphics.y = p.startY + p.vy * age * 0.1 + 0.5 * age * 0.01;
        p.graphics.alpha = 1 - progress;
      });
      const uniqueParticleIndices = [...new Set(particlesToRemove)];
      uniqueParticleIndices.reverse().forEach((index) => {
        const p = particlesRef.current[index];
        if (p.graphics.parent) {
          p.graphics.parent.removeChild(p.graphics);
        }
        p.graphics.destroy();
        particlesRef.current.splice(index, 1);
      });
    });
    isInitializedRef.current = true;
  }, [isPlaying, onScoreChange, spawnParticleBurst]);
  // Touch/pointer handlers
  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    if (!isPlaying) return;
    touchStartXRef.current = e.clientX;
    if (basketRef.current && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const touchX = e.clientX - rect.left;
      const worldX = (touchX / rect.width) * WORLD_WIDTH;
      basketRef.current.x = worldX;
    }
  }, [isPlaying]);
  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!isPlaying || touchStartXRef.current === null) return;
    if (basketRef.current && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const touchX = e.clientX - rect.left;
      const worldX = (touchX / rect.width) * WORLD_WIDTH;
      // Smooth follow with lerp
      basketRef.current.x += (worldX - basketRef.current.x) * TOUCH_SENSITIVITY * 0.3;
      // Clamp
      basketRef.current.x = Math.max(
        BASKET_WIDTH / 2,
        Math.min(WORLD_WIDTH - BASKET_WIDTH / 2, basketRef.current.x)
      );
    }
  }, [isPlaying]);
  const handlePointerUp = useCallback(() => {
    touchStartXRef.current = null;
  }, []);
  // Keyboard handlers
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      keysRef.current.add(e.key.toLowerCase());
    };
    const handleKeyUp = (e: KeyboardEvent) => {
      keysRef.current.delete(e.key.toLowerCase());
    };
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);
  // Initialize Pixi app
  useEffect(() => {
    let cancelled = false;
    let destroyed = false;
    const init = async () => {
      if (cancelled || destroyed) return;
      await initGame();
    };
    init();
    return () => {
      cancelled = true;
      if (appRef.current && !destroyed) {
        destroyed = true;
        // Clean up stars
        starsRef.current.forEach(star => {
          star.sprite.destroy({ children: true });
        });
        starsRef.current = [];
        // Clean up particles
        particlesRef.current.forEach(p => {
          p.graphics.destroy();
        });
        particlesRef.current = [];
        // Clean up app
        if (starContainerRef.current) {
          starContainerRef.current.destroy({ children: true });
        }
        if (particleContainerRef.current) {
          particleContainerRef.current.destroy({ children: true });
        }
        appRef.current.destroy(true, { children: true });
        appRef.current = null;
        isInitializedRef.current = false;
      }
    };
  }, [initGame]);
  // Reset game state when game starts
  useEffect(() => {
    if (isPlaying && basketRef.current) {
      basketRef.current.x = WORLD_WIDTH / 2;
      // Clear all stars
      starsRef.current.forEach(star => {
        if (star.sprite.parent) {
          star.sprite.parent.removeChild(star.sprite);
        }
        star.sprite.destroy({ children: true });
      });
      starsRef.current = [];
      // Clear particles
      particlesRef.current.forEach(p => {
        if (p.graphics.parent) {
          p.graphics.parent.removeChild(p.graphics);
        }
        p.graphics.destroy();
      });
      particlesRef.current = [];
      lastSpawnRef.current = Date.now();
    }
  }, [isPlaying]);
  return (
    <div
      ref={containerRef}
      className="w-full h-full touch-none select-none"
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerLeave={handlePointerUp}
    />
  );
}